import type { Insight } from "@tandemdiabetes/sugarmate-api-react-client"
import { InsightKey } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  Modal,
  ModalContentForm,
  ModalBody,
  ModalHeading,
  ModalCloseX,
  ActionSubmit,
  Dropdown,
  DropdownCaret,
  DropdownOption,
  DropdownSelect,
  useToasterContext,
  DropdownCurrentSelection,
} from "@tandemdiabetes/sugarmate-react-components"
import type {
  ChangeEventHandler,
  FormEventHandler,
  ReactNode,
} from "react"
import {
  useState,
  useCallback,
  useMemo,
} from "react"
import { defaultInsightRangeByKey } from "../defaultInsightRangeByKey"
import { insightRangesByKey } from "../insightRangesByKey"
import { InsightRangeString } from "../InsightRangeString"
import { useInsightIcon } from "../useInsightIcon"
import styles from "./EditInsightModal.module.scss"
import { EditInsightModalContext } from "./EditInsightModalContext"
import { DiscardChangesDialog } from "src/components"
import {
  useOverlayLoader,
  usePolyglot,
} from "src/contexts"
import {
  useUpdateInsight,
  useEditableType,
} from "src/hooks"
import { EditInsightErrorToast } from "src/toasts"
import { injectProps } from "src/utils"

const polyglotPrefix = "pages.home.insights."

interface Props {
  children: ReactNode;
  availableInsightRangesByKey: Record<InsightKey, Insight["range"][]>;
}

export function EditInsightModal(props: Props) {
  const polyglot = usePolyglot()
  const updateInsight = useUpdateInsight()
  const { showToast } = useToasterContext()
  const { setIsOverlayLoaderVisible } = useOverlayLoader()

  const {
    original: originalInsight,
    current: currentInsight,
    hasChanged: insightHasChanged,
    save: resetCurrentInsight,
    update: setCurrentInsight,
  } = useEditableType<Insight>({
    key: InsightKey.GlucoseAverageReading,
    range: insightRangesByKey[InsightKey.GlucoseAverageReading][0],
  } as Insight)

  const [
    isModalOpen,
    setIsModalOpen,
  ] = useState<boolean>(false)

  const [
    isDiscardOpen,
    setIsDiscardOpen,
  ] = useState<boolean>(false)

  const InsightIcon = useInsightIcon(currentInsight.key)

  const openEditInsightModal = useCallback(
    (insight: Insight) => {
      resetCurrentInsight(insight)
      setIsModalOpen(true)
    },
    [
      setIsModalOpen,
      resetCurrentInsight,
    ],
  )

  const closeEditInsightModal = useCallback(
    () => {
      setIsModalOpen(false)
    },
    [setIsModalOpen],
  )

  const contextValue = useMemo(
    () => ({
      openEditInsightModal,
    }),
    [openEditInsightModal],
  )

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (ev) => {
      ev.preventDefault()
      ev.stopPropagation()

      void (async () => {
        const ToastComponent = injectProps(
          EditInsightErrorToast,
          { insightKey: currentInsight.key },
        )

        setIsOverlayLoaderVisible(true)

        try {
          if (!updateInsight) {
            throw new Error("cannot update insight")
          }

          await updateInsight(
            originalInsight,
            currentInsight,
          )

          setIsModalOpen(false)
        } catch {
          showToast(
            "edit-insight-error-toast",
            ToastComponent,
          )
        }

        setIsOverlayLoaderVisible(false)
      })()
    },
    [
      currentInsight,
      originalInsight,
      updateInsight,
      showToast,
      setIsOverlayLoaderVisible,
    ],
  )

  const onOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen) {
        if (insightHasChanged) {
          setIsDiscardOpen(true)
        } else {
          closeEditInsightModal()
        }
      }
    },
    [
      insightHasChanged,
      setIsDiscardOpen,
      closeEditInsightModal,
    ],
  )

  const onRangeChange: ChangeEventHandler<HTMLSelectElement> = useCallback(
    (e) => {
      const range = Number(e.target.value)

      setCurrentInsight({
        ...currentInsight,
        range,
      } as Insight)
    },
    [
      currentInsight,
      setCurrentInsight,
    ],
  )

  const availableInsightRanges = props.availableInsightRangesByKey[currentInsight.key]
  const allInsightRanges = insightRangesByKey[currentInsight.key]

  const rangeDisabledMap: Record<number, boolean> = useMemo(
    () => {
      const map: Record<number, boolean> = {}

      allInsightRanges.forEach(
        (insightRange) => {
          if (insightRange !== null) {
            map[insightRange] = !availableInsightRanges.includes(insightRange)
          }
        },
      )

      return map
    },
    [
      availableInsightRanges,
      allInsightRanges,
    ],
  )

  // some tiles that can only have one range won't have a range set, so we use the default range
  const currentRange = currentInsight.range ?? defaultInsightRangeByKey[currentInsight.key]

  return (
    <EditInsightModalContext.Provider value={contextValue}>
      <Modal
        open={isModalOpen}
        onOpenChange={onOpenChange}

      >
        {props.children}
        <ModalContentForm
          onSubmit={onSubmit}
          id={`edit-modal-${currentInsight.key}`}
        >
          <ModalHeading>
            <div
              className={styles.modalHeading}
              id="edit-insight-modal-heading"
            >
              {InsightIcon && <InsightIcon className={styles.modalHeadingIcon} />}
              {polyglot.t(`${polyglotPrefix}cta_label.${currentInsight.key}`)}
            </div>
            <ModalCloseX id={"cancel-button"}/>
          </ModalHeading>
          <ModalBody className={styles.modalBody}>
            {polyglot.t(`${polyglotPrefix}cta_subtext.${currentInsight.key}`)}
            <Dropdown id="edit-insight-dropdown">
              <DropdownSelect
                value={`${currentRange}`}
                onChange={onRangeChange}
                id="insight-range-dropdown"
                required
              >
                {allInsightRanges.map(
                  (insightRange) => {
                    const checked = insightRange === currentRange
                    const wasChecked = insightRange === originalInsight.range
                    const disabled = !checked && !wasChecked && (insightRange === null || rangeDisabledMap[insightRange])

                    return (
                      <DropdownOption
                        key={`insight-range-${insightRange}`}
                        value={`${insightRange}`}
                        disabled={disabled}
                      >
                        <InsightRangeString range={insightRange} />
                      </DropdownOption>
                    )
                  },
                )}
              </DropdownSelect>
              <DropdownCurrentSelection id="selected-option"/>
              <DropdownCaret />
            </Dropdown>
            <ActionSubmit
              value={polyglot.t(`${polyglotPrefix}edit_insight_modal.submit_cta`)}
              disabled={!insightHasChanged}
              id="submit-button"
            />
          </ModalBody>
          <DiscardChangesDialog
            open={isDiscardOpen}
            setOpen={setIsDiscardOpen}
            discardChanges={closeEditInsightModal}
          />
        </ModalContentForm>
      </Modal>
    </EditInsightModalContext.Provider>
  )
}
